import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useShipmentPaid } from '#/entities/shipment'
import {
  MarketRateCheck,
  MarketRateDialog,
  useSuggestedPrice,
} from '#/features/market-rate'
import type { MarketRateRow } from '#/features/market-rate'
import { PaymentEditForm } from '#/features/payment-edit'
import type { PaymentFormData } from '#/features/payment-edit'
import type { ShipmentDetail } from '#/shared/api/generated/shipment/models'
import { Button } from '#/shared/ui/button'
import { PencilIcon, PlusIcon } from '#/shared/ui/icons'
import { PriceCheckBadge } from '#/shared/ui/price-check-badge'
import { Tooltip } from '#/shared/ui/tooltip'
import { moneyToDisplay, moneyToFormString } from '../lib/money'
import { paymentToFormDefaults } from '../lib/to-form-defaults'

interface PaymentCardProps {
  shipment?: ShipmentDetail
  onSave?: (data: PaymentFormData) => void | Promise<void>
  onApplyTariff?: (tariff: number) => void | Promise<void>
  showCarrierDetails?: boolean
  priceMode?: boolean
}

function hasPositiveAmount(raw: number | string | null | undefined): boolean {
  if (raw == null || raw === '') return false
  const num = typeof raw === 'number' ? raw : Number(raw)
  return Number.isFinite(num) && num > 0
}

function toFiniteNumber(
  raw: number | string | null | undefined,
): number | null {
  if (raw == null || raw === '') return null
  const num = typeof raw === 'number' ? raw : Number(raw)
  return Number.isFinite(num) ? num : null
}

function dodFromTariffDeposit(
  tariff: number | string | null | undefined,
  deposit: number | string | null | undefined,
): number | null {
  const tariffNum = toFiniteNumber(tariff)
  if (tariffNum == null) return null
  return tariffNum - (toFiniteNumber(deposit) ?? 0)
}

function PaymentRow({
  label,
  value,
  badge,
}: {
  label: React.ReactNode
  value: string
  badge?: React.ReactNode
}) {
  return (
    <div className="flex h-8 items-center gap-5 px-5">
      <span className="w-25 shrink-0 text-sm whitespace-nowrap text-gray-500">
        {label}
      </span>
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-900">{value}</span>
        {badge}
      </div>
    </div>
  )
}

function PaidBadge({ amount }: { amount: string }) {
  const { t } = useTranslation()
  return (
    <span className="inline-flex h-6 items-center rounded bg-green-50 px-1.5 text-xs font-medium text-green-500">
      {t('orderDetails.payment.paid', { amount })}
    </span>
  )
}

function NoteRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-h-8 items-center px-5 py-1">
      <div className="flex min-h-6 flex-1 items-center gap-1.25 rounded-md bg-gray-100 px-1.25">
        <span className="shrink-0 text-xs text-gray-900">{label}</span>
        <span className="text-sm text-gray-900">{value}</span>
      </div>
    </div>
  )
}

function CarrierPayLabel() {
  const { t } = useTranslation()
  return (
    <>
      {t('orderDetails.payment.fields.carrierPay')} /{' '}
      <Tooltip content={t('orderDetails.payment.fields.dodFull')}>
        <span className="cursor-help">
          {t('orderDetails.payment.fields.dod')}
        </span>
      </Tooltip>
    </>
  )
}

function DepositRow({ shipment }: { shipment?: ShipmentDetail }) {
  const { t } = useTranslation()
  const deposit = shipment?.deposit
  const badge = hasPositiveAmount(deposit) ? (
    <PaidBadge amount={moneyToDisplay(deposit)} />
  ) : undefined
  return (
    <PaymentRow
      label={t('orderDetails.payment.fields.deposit')}
      value={moneyToDisplay(deposit)}
      badge={badge}
    />
  )
}

function PaidLabel() {
  const { t } = useTranslation()
  return (
    <Tooltip content={t('orderDetails.payment.fields.paidFull')}>
      <span className="cursor-help">
        {t('orderDetails.payment.fields.paid')}
      </span>
    </Tooltip>
  )
}

/**
 * Money actually taken across every payment on the order, net of refunds —
 * `deposit` above is only what someone typed at booking. Labelled "Paid", not
 * "Collected": the Payment tab footer already shows a gross COLLECTED beside a
 * separate REFUNDED, and two fields reading "Collected" with different numbers
 * on one screen reads as a bug.
 */
function PaidRow({ shipment }: { shipment?: ShipmentDetail }) {
  const { paid } = useShipmentPaid(shipment?.id)
  return <PaymentRow label={<PaidLabel />} value={paid ?? '—'} />
}

function CarrierPayRow({ shipment }: { shipment?: ShipmentDetail }) {
  const dod = dodFromTariffDeposit(shipment?.tariff, shipment?.deposit)
  return (
    <PaymentRow
      label={<CarrierPayLabel />}
      value={`${moneyToDisplay(shipment?.carrier_pay)} / ${moneyToDisplay(dod)}`}
    />
  )
}

function PaymentRows({
  shipment,
  note,
  showCarrierDetails,
}: {
  shipment?: ShipmentDetail
  note: string
  showCarrierDetails: boolean
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col">
      <PaymentRow
        label={t('orderDetails.payment.fields.tariff')}
        value={moneyToDisplay(shipment?.tariff)}
      />
      <DepositRow shipment={shipment} />
      <PaidRow shipment={shipment} />
      {showCarrierDetails && (
        <>
          <CarrierPayRow shipment={shipment} />
          {note && (
            <NoteRow
              label={t('orderDetails.payment.fields.note')}
              value={note}
            />
          )}
        </>
      )}
    </div>
  )
}

function PriceEmptyState({ onAdd }: { onAdd: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center justify-between gap-2.5 px-5">
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-semibold text-gray-900">
          {t('orderDetails.payment.noPriceYet')}
        </span>
        <span className="text-xs text-gray-500">
          {t('orderDetails.payment.noPriceDescription')}
        </span>
      </div>
      <Button
        type="button"
        variant="warning"
        size="xs"
        pressEffect
        className="px-3.5"
        onClick={onAdd}
      >
        <PlusIcon className="size-3.5" />
        {t('orderDetails.payment.addPrice')}
      </Button>
    </div>
  )
}

function PaymentCardHeader({
  shipment,
  priceMode,
  onEdit,
  onOpenMarketRate,
}: {
  shipment?: ShipmentDetail
  priceMode: boolean
  onEdit: () => void
  onOpenMarketRate: () => void
}) {
  const { t } = useTranslation()
  return (
    <header className="flex flex-col gap-1.25">
      <div className="flex h-8 items-center justify-between gap-2.5 px-5 pb-1.25">
        <h3 className="flex items-center gap-2.5 text-base font-semibold text-gray-900">
          {priceMode
            ? t('orderDetails.payment.priceTitle')
            : t('orderDetails.payment.title')}
          <MarketRateCheck shipmentId={shipment?.id} />
          <PriceCheckBadge
            label={t('orderDetails.payment.priceCheck')}
            onClick={onOpenMarketRate}
          />
        </h3>
        {!priceMode && (
          <button
            type="button"
            aria-label={t('orderDetails.payment.title')}
            onClick={onEdit}
            className="flex size-8 cursor-pointer items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
          >
            <PencilIcon className="size-3.5" />
          </button>
        )}
      </div>
      <div className="h-px w-full bg-gray-200" />
    </header>
  )
}

function PaymentCardView({
  shipment,
  onEdit,
  onApplyTariff,
  onSeedFromRate,
  showCarrierDetails,
  priceMode,
}: {
  shipment?: ShipmentDetail
  onEdit: () => void
  onApplyTariff?: (tariff: number) => void | Promise<void>
  onSeedFromRate?: (rate: number) => void
  showCarrierDetails: boolean
  priceMode: boolean
}) {
  const [marketRateOpen, setMarketRateOpen] = useState(false)
  const note = shipment?.payment_notes ?? ''
  const suggestion = useSuggestedPrice({
    shipmentId: shipment?.id,
    enabled: marketRateOpen,
  })

  const handleUseRate = (row: MarketRateRow) => {
    const rate = row.actualPrice ?? row.listedPrice
    if (priceMode) {
      onSeedFromRate?.(rate)
      return
    }
    void onApplyTariff?.(rate)
  }

  return (
    <>
      <div className="flex w-full flex-col gap-2.5 rounded-md border border-gray-200 bg-white pt-2.5 pb-5">
        <PaymentCardHeader
          shipment={shipment}
          priceMode={priceMode}
          onEdit={onEdit}
          onOpenMarketRate={() => setMarketRateOpen(true)}
        />

        {priceMode ? (
          <PriceEmptyState onAdd={onEdit} />
        ) : (
          <PaymentRows
            shipment={shipment}
            note={note}
            showCarrierDetails={showCarrierDetails}
          />
        )}
      </div>
      <MarketRateDialog
        open={marketRateOpen}
        onOpenChange={setMarketRateOpen}
        stats={suggestion.stats}
        rows={suggestion.rows}
        isLoading={suggestion.isLoading}
        errorMessage={suggestion.errorMessage}
        onUseRate={handleUseRate}
      />
    </>
  )
}

const EMPTY_PRICE_DEFAULTS: PaymentFormData = {
  totalTariff: '',
  deposit: '',
  carrierFee: '',
  paymentMethod: '',
  paymentTerms: '',
  balanceMethod: '',
  balanceTime: '',
  balanceTermBegins: '',
  paymentNote: '',
}

export function PaymentCard({
  shipment,
  onSave,
  onApplyTariff,
  showCarrierDetails = true,
  priceMode = false,
}: PaymentCardProps) {
  const [editing, setEditing] = useState(false)
  const [seededDefaults, setSeededDefaults] = useState<
    PaymentFormData | undefined
  >()

  const handleSubmit = async (data: PaymentFormData) => {
    try {
      await onSave?.(data)
      setEditing(false)
      setSeededDefaults(undefined)
    } catch {
      // keep the form open so the user can correct and retry
    }
  }

  const handleSeedFromRate = (rate: number) => {
    setSeededDefaults({
      totalTariff: moneyToFormString(rate),
      deposit: '0.00',
      carrierFee: '',
      paymentMethod: '',
      paymentTerms: '',
      balanceMethod: '',
      balanceTime: '',
      balanceTermBegins: '',
      paymentNote: '',
    })
    setEditing(true)
  }

  if (editing) {
    const defaults =
      seededDefaults ??
      (priceMode ? EMPTY_PRICE_DEFAULTS : paymentToFormDefaults(shipment))
    return (
      <PaymentEditForm
        shipmentId={shipment?.id}
        defaultValues={defaults}
        onSubmit={handleSubmit}
        onCancel={() => {
          setEditing(false)
          setSeededDefaults(undefined)
        }}
        showCarrierDetails={showCarrierDetails}
        priceMode={priceMode}
      />
    )
  }

  return (
    <PaymentCardView
      shipment={shipment}
      onEdit={() => setEditing(true)}
      onApplyTariff={onApplyTariff}
      onSeedFromRate={handleSeedFromRate}
      showCarrierDetails={showCarrierDetails}
      priceMode={priceMode}
    />
  )
}
